import { MarketIntelligence, SentimentData, TalentAvailability, ReplacementDifficulty, CompetitorActivity } from '../types';

// Generate historical sentiment data for the last 6 months
const generateSentimentHistory = (): SentimentData[] => {
  const sources: Array<'LinkedIn' | 'Glassdoor' | 'Indeed' | 'Twitter' | 'News'> = 
    ['LinkedIn', 'Glassdoor', 'Indeed', 'Twitter', 'News'];
  const history: SentimentData[] = [];
  
  for (let i = 180; i >= 0; i -= 7) { // Weekly data for 6 months
    const date = new Date();
    date.setDate(date.getDate() - i);
    
    sources.forEach(source => {
      const baseVolume = source === 'LinkedIn' ? 450 : 
                        source === 'Twitter' ? 1200 : 
                        source === 'Glassdoor' ? 85 : 
                        source === 'Indeed' ? 120 : 65;
      
      const volume = Math.floor(baseVolume + (Math.random() - 0.5) * baseVolume * 0.3);
      const positive = Math.floor(35 + Math.random() * 25);
      const negative = Math.floor(10 + Math.random() * 15);
      const neutral = 100 - positive - negative;
      
      history.push({
        date: date.toISOString().split('T')[0],
        positive,
        neutral,
        negative,
        volume,
        source
      });
    });
  }
  
  return history;
};

const talentAvailabilityData: TalentAvailability[] = [
  {
    role: 'Senior Software Engineer',
    location: 'Berlin, Germany',
    totalCandidates: 3450,
    activelyLooking: 485,
    passiveCandidates: 2965,
    averageSalary: 95000,
    salaryRange: { min: 75000, max: 120000, median: 95000 },
    topSkills: ['React', 'TypeScript', 'Node.js', 'AWS', 'Python'],
    difficultyIndex: 'MEDIUM',
    trend: 'stable',
    lastUpdated: new Date().toISOString()
  },
  {
    role: 'Data Scientist',
    location: 'Munich, Germany',
    totalCandidates: 1280,
    activelyLooking: 156,
    passiveCandidates: 1124,
    averageSalary: 105000,
    salaryRange: { min: 80000, max: 130000, median: 105000 },
    topSkills: ['Python', 'Machine Learning', 'TensorFlow', 'SQL', 'Statistics'],
    difficultyIndex: 'HIGH',
    trend: 'decreasing',
    lastUpdated: new Date().toISOString()
  },
  {
    role: 'Product Manager',
    location: 'Frankfurt, Germany',
    totalCandidates: 890,
    activelyLooking: 112,
    passiveCandidates: 778,
    averageSalary: 85000,
    salaryRange: { min: 70000, max: 105000, median: 85000 },
    topSkills: ['Product Strategy', 'Agile', 'Analytics', 'Stakeholder Management'],
    difficultyIndex: 'HIGH',
    trend: 'stable',
    lastUpdated: new Date().toISOString()
  },
  {
    role: 'DevOps Engineer',
    location: 'Berlin, Germany',
    totalCandidates: 620,
    activelyLooking: 95,
    passiveCandidates: 525,
    averageSalary: 90000,
    salaryRange: { min: 70000, max: 110000, median: 90000 },
    topSkills: ['Kubernetes', 'Docker', 'CI/CD', 'AWS', 'Terraform'],
    difficultyIndex: 'CRITICAL',
    trend: 'decreasing',
    lastUpdated: new Date().toISOString()
  },
  {
    role: 'UX Designer',
    location: 'Hamburg, Germany',
    totalCandidates: 1560,
    activelyLooking: 234,
    passiveCandidates: 1326,
    averageSalary: 70000,
    salaryRange: { min: 55000, max: 85000, median: 70000 },
    topSkills: ['Figma', 'User Research', 'Prototyping', 'Design Systems'],
    difficultyIndex: 'LOW',
    trend: 'increasing',
    lastUpdated: new Date().toISOString()
  },
  {
    role: 'Machine Learning Engineer',
    location: 'Berlin, Germany',
    totalCandidates: 420,
    activelyLooking: 48,
    passiveCandidates: 372,
    averageSalary: 115000,
    salaryRange: { min: 90000, max: 140000, median: 115000 },
    topSkills: ['Python', 'TensorFlow', 'PyTorch', 'MLOps', 'Deep Learning'],
    difficultyIndex: 'CRITICAL',
    trend: 'decreasing',
    lastUpdated: new Date().toISOString()
  }
];

const replacementDifficultyData: ReplacementDifficulty[] = [
  {
    role: 'Senior Software Engineer',
    location: 'Berlin, Germany',
    seniorityLevel: 'Senior',
    requiredSkills: ['React', 'TypeScript', 'Node.js', 'AWS'],
    difficultyScore: 65,
    difficultyLevel: 'MEDIUM',
    marketAvailability: 485,
    estimatedTimeToFill: 42,
    estimatedCostToReplace: 25000,
    attritionRisk: 35,
    isCritical: false,
    recommendations: [
      'Consider internal promotion from mid-level engineers',
      'Offer competitive salary package above market median',
      'Highlight remote work flexibility in job postings'
    ]
  },
  {
    role: 'Data Scientist',
    location: 'Munich, Germany',
    seniorityLevel: 'Senior',
    requiredSkills: ['Python', 'Machine Learning', 'TensorFlow', 'Statistics'],
    difficultyScore: 78,
    difficultyLevel: 'HIGH',
    marketAvailability: 156,
    estimatedTimeToFill: 65,
    estimatedCostToReplace: 35000,
    attritionRisk: 42,
    isCritical: true,
    recommendations: [
      'Build talent pipeline through university partnerships',
      'Consider remote candidates from other regions',
      'Implement retention program for current data scientists',
      'Offer learning & development opportunities in AI/ML'
    ]
  },
  {
    role: 'DevOps Engineer',
    location: 'Berlin, Germany',
    seniorityLevel: 'Senior',
    requiredSkills: ['Kubernetes', 'Docker', 'CI/CD', 'AWS', 'Terraform'],
    difficultyScore: 88,
    difficultyLevel: 'CRITICAL',
    marketAvailability: 95,
    estimatedTimeToFill: 85,
    estimatedCostToReplace: 40000,
    attritionRisk: 52,
    isCritical: true,
    recommendations: [
      'URGENT: Implement immediate retention measures',
      'Consider contracting/consulting to fill immediate gaps',
      'Develop internal training program for junior engineers',
      'Partner with specialized recruiting firms',
      'Review and increase compensation to top quartile'
    ]
  },
  {
    role: 'Machine Learning Engineer',
    location: 'Berlin, Germany',
    seniorityLevel: 'Senior',
    requiredSkills: ['Python', 'TensorFlow', 'PyTorch', 'MLOps', 'Deep Learning'],
    difficultyScore: 92,
    difficultyLevel: 'CRITICAL',
    marketAvailability: 48,
    estimatedTimeToFill: 95,
    estimatedCostToReplace: 50000,
    attritionRisk: 48,
    isCritical: true,
    recommendations: [
      'CRITICAL: Only 48 actively looking candidates in market',
      'Consider international relocation packages',
      'Build relationships with ML research communities',
      'Offer equity/long-term incentives',
      'Create ML Center of Excellence to attract top talent'
    ]
  }
];

const competitorActivityData: CompetitorActivity[] = [
  {
    company: 'TechCorp',
    hiringVolume: 145,
    openRoles: 89,
    averageSalary: 98000,
    trend: 'increasing',
    topRoles: ['Software Engineer', 'Data Scientist', 'Product Manager'],
    lastUpdated: new Date().toISOString()
  },
  {
    company: 'DataDrive',
    hiringVolume: 78,
    openRoles: 45,
    averageSalary: 105000,
    trend: 'stable',
    topRoles: ['Data Scientist', 'ML Engineer', 'Data Engineer'],
    lastUpdated: new Date().toISOString()
  },
  {
    company: 'CloudNine',
    hiringVolume: 92,
    openRoles: 56,
    averageSalary: 92000,
    trend: 'increasing',
    topRoles: ['DevOps Engineer', 'Cloud Architect', 'SRE'],
    lastUpdated: new Date().toISOString()
  },
  {
    company: 'InnovateLabs',
    hiringVolume: 34,
    openRoles: 22,
    averageSalary: 88000,
    trend: 'decreasing',
    topRoles: ['Product Designer', 'UX Researcher', 'Frontend Engineer'],
    lastUpdated: new Date().toISOString()
  },
  {
    company: 'FutureScale',
    hiringVolume: 156,
    openRoles: 102,
    averageSalary: 95000,
    trend: 'increasing',
    topRoles: ['Full Stack Engineer', 'Engineering Manager', 'QA Engineer'],
    lastUpdated: new Date().toISOString()
  }
];

export const mockMarketIntelligence: MarketIntelligence = {
  sentimentAnalysis: {
    current: {
      period: 'Last 7 days',
      overallSentiment: 72,
      change: 5,
      volume: 2458,
      topMentions: [
        'innovative culture',
        'great benefits',
        'work-life balance',
        'career growth',
        'remote flexibility'
      ],
      correlatedEvents: [
        'Q4 earnings announcement (+8% sentiment)',
        'New product launch (+12% sentiment)',
        'Flexible work policy update (+15% sentiment)'
      ]
    },
    history: generateSentimentHistory(),
    sources: [
      {
        source: 'LinkedIn',
        sentiment: 78,
        volume: 856,
        lastUpdated: new Date().toISOString()
      },
      {
        source: 'Glassdoor',
        sentiment: 82,
        volume: 234,
        lastUpdated: new Date().toISOString()
      },
      {
        source: 'Indeed',
        sentiment: 75,
        volume: 156,
        lastUpdated: new Date().toISOString()
      },
      {
        source: 'Twitter',
        sentiment: 65,
        volume: 1890,
        lastUpdated: new Date().toISOString()
      },
      {
        source: 'News',
        sentiment: 88,
        volume: 45,
        lastUpdated: new Date().toISOString()
      }
    ]
  },
  talentAvailability: talentAvailabilityData,
  replacementDifficulty: replacementDifficultyData,
  competitorActivity: competitorActivityData,
  marketTrends: {
    hotSkills: [
      'Artificial Intelligence',
      'Machine Learning',
      'Cloud Architecture',
      'Kubernetes',
      'React',
      'TypeScript',
      'Data Engineering',
      'DevOps'
    ],
    emergingRoles: [
      'AI Ethics Officer',
      'Prompt Engineer',
      'Climate Tech Specialist',
      'Web3 Developer',
      'Quantum Computing Engineer'
    ],
    decliningRoles: [
      'Manual QA Tester',
      'Data Entry Specialist',
      'Traditional System Administrator',
      'Print Designer'
    ],
    salaryTrends: [
      { role: 'Machine Learning Engineer', change: 15 },
      { role: 'DevOps Engineer', change: 12 },
      { role: 'Data Scientist', change: 10 },
      { role: 'Product Manager', change: 8 },
      { role: 'Software Engineer', change: 7 },
      { role: 'UX Designer', change: 5 },
      { role: 'QA Engineer', change: 3 }
    ]
  },
  lastRefresh: new Date().toISOString(),
  nextScheduledRefresh: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // Next week
};

// Helper function to get sentiment score color
export const getSentimentColor = (score: number): string => {
  if (score >= 70) return '#10B981'; // Green
  if (score >= 40) return '#F59E0B'; // Yellow
  return '#EF4444'; // Red
};

// Helper function to get difficulty color
export const getDifficultyColor = (level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'): string => {
  switch (level) {
    case 'LOW': return '#10B981';
    case 'MEDIUM': return '#F59E0B';
    case 'HIGH': return '#F97316';
    case 'CRITICAL': return '#EF4444';
  }
};

// Helper function to format large numbers
export const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};